import React, { useState, useMemo, useCallback } from 'react';
import { Box, Spinner } from '@apia/theme';
import { useUpdateEffect } from 'ahooks';
import { FaFileUpload } from '@meronex/icons/fa';
import UploaderContext from './uploader/context';
import {
  TUploader,
  TUploaderContext,
  TUploaderFileInfo,
  TUploaderState,
} from './uploader/types';
import UploaderApi, { isOneClickUploadEnabled } from './uploader/UploaderApi';
import DropZone, { dropzoneEmitter } from './uploader/DropZone';
import DefaultUploadView from './uploader/DefaultUploadView';
import Progress from './uploader/Progress';
import { useAppSelector } from '../../hooks/hooks';
import { documentUploadModalId } from '../apia/common/modals/documentUploadModal';
import { addBoundary } from '../../utils/number';
import { notify } from './notifications/util';
import IconButton from './buttons/IconButton';
import usePrevious from '../../hooks/usePrevious';

const emptyArrayPrimitive: TUploaderFileInfo[] = Object.assign([], {
  isInitial: true,
});
const emptyArray = Object.freeze<TUploaderFileInfo[]>(
  emptyArrayPrimitive,
) as unknown as TUploaderFileInfo[];

const Uploader = React.forwardRef<HTMLDivElement, TUploader>(
  (
    {
      allowDrop,
      Api = UploaderApi,
      children = <DefaultUploadView />,
      dropzoneId,
      dropzoneTooltip,
      filesList,
      id,
      initialFilesList,
      onFilesListLoad,
      isReadonly,
      langId,
      maxFiles,
      noModify,
      onFilesListChange,
      translatingFile,
      type,
      ariaLabel,
      ariaDescription,
      autoFocus,
      title,
      'data-testid': testid,
      ...dropzoneOptions
    },
    ref,
  ) => {
    const [contextState, setContext] = useState<TUploaderState>({
      loading: true,
      uploadingFiles: [],
      progress: 100,
      type,
    });

    const [api, setApi] = React.useState<UploaderApi | null>(null);

    const files = useAppSelector((global) => {
      if (api) {
        if (langId === undefined)
          return global.fileUploadSlice.uploaders[api.id].filesArray;
        if (translatingFile)
          return [
            global.fileUploadSlice.uploaders[api.id].translatedFiles[
              translatingFile.docId
            ]?.[langId],
          ].filter((current) => !!current);
      }

      return emptyArray;
    });

    const hasShoutFirst = React.useRef(false);
    React.useEffect(() => {
      if (files !== emptyArray) {
        onFilesListLoad?.(files);

        if (hasShoutFirst.current) {
          onFilesListChange?.(files);
        }

        hasShoutFirst.current = true;
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    useUpdateEffect(() => {
      if (filesList) api?.filesActions.updateArray(filesList);
    }, [filesList]);

    const contextValue: TUploaderContext = useMemo(
      () => ({
        api: api as UploaderApi,
        ...contextState,
        disabled: !!dropzoneOptions.disabled,
        isReadonly: !!isReadonly,
        langId,
        setContext,
        shownFiles: files,
        translatingFile,
      }),
      [
        api,
        contextState,
        dropzoneOptions.disabled,
        isReadonly,
        langId,
        files,
        translatingFile,
      ],
    );

    const loading = useCallback(
      (currentLoading: boolean) =>
        setContext((context) => ({ ...context, loading: currentLoading })),
      [],
    );

    const isLoadingModal = useAppSelector(
      (global) =>
        global.remoteModalsSlice.modals[documentUploadModalId]?.isLoading,
    );

    React.useEffect(() => {
      async function fetch() {
        if (Api instanceof UploaderApi) {
          setApi(Api);
        } else {
          const newApi = new Api(type, id);
          await newApi.initialLoad();
          if (initialFilesList)
            newApi?.filesActions.updateArray(initialFilesList);
          setApi(newApi);
        }
        loading(false);
      }
      void fetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Api, id, type]);

    const processFiles = useCallback<
      NonNullable<React.ComponentProps<typeof DropZone>['onDropAccepted']>
    >(
      async (outerDroppedFiles) => {
        const droppedFiles = outerDroppedFiles;
        const versioningFiles = contextValue.langId
          ? []
          : api?.filesArray.filter((current) =>
              droppedFiles.find(
                (search) => search.name === (current.name || current.docName),
              ),
            );
        if (versioningFiles?.length === 1 && droppedFiles?.length === 1) {
          await api?.version(
            versioningFiles[0],
            (newProgress) => {
              setContext((context) => ({
                ...context,
                progress: newProgress * 0.8,
              }));
            },
            {
              newFiles: droppedFiles,
            },
          );
          setContext((context) => ({ ...context, progress: 100 }));
        } else {
          if ((versioningFiles?.length ?? 0) > 0) {
            notify({
              message: `${window.LBL_DROP_FILE_REPEATED}: ${
                versioningFiles
                  ?.map((current) => current.name || current.docName || '')
                  .join(', ') ?? 'THIS STRING WILL NEVER BE REACHED'
              }.`,
              type: 'warning',
            });
          }

          const uploadingFiles = droppedFiles.filter(
            (current) =>
              !versioningFiles?.find(
                (search) => search.name || search.docName === current.name,
              ),
          );
          const actualMaxFiles = addBoundary(
            (api?.currentConfiguration?.allowMultiple
              ? maxFiles ?? Infinity
              : 1) - files.length,
            0,
          );
          const acceptedFilesArray = uploadingFiles.slice(0, actualMaxFiles);
          await api?.saveDroppedFiles(
            acceptedFilesArray,
            (newProgress) => {
              setContext((context) => ({
                ...context,
                progress: newProgress * 0.8,
              }));
            },
            { langId, translatingFile, shouldReset: true },
          );

          setContext((context) => ({ ...context, progress: 100 }));
        }
      },
      [
        api,
        contextValue.langId,
        files.length,
        langId,
        maxFiles,
        translatingFile,
      ],
    );

    const onClick = React.useMemo(
      () => () => {
        if (isOneClickUploadEnabled(api?.configurationObject.oneClickUpload))
          dropzoneEmitter.emit('open', { id: dropzoneId as string });
        else api?.openModal({ langId, translatingFile });
      },
      [api, dropzoneId, langId, translatingFile],
    );

    const dropzoneSx = React.useMemo(
      () => ({
        display:
          (files.length ?? 0) < (maxFiles ?? Number.POSITIVE_INFINITY) &&
          !noModify &&
          !isReadonly
            ? 'block'
            : 'none',
      }),
      [files.length, isReadonly, maxFiles, noModify],
    );

    const dropzoneRef = React.useRef<HTMLElement>(null);
    const lastFilesLength = usePrevious(files.length);
    React.useEffect(() => {
      if (files.length === 0 && (lastFilesLength.current ?? 0) > 0) {
        (
          dropzoneRef.current?.querySelector('[tabindex="0"]') as HTMLElement
        )?.focus();
      }
    }, [files.length, lastFilesLength]);
    if (!api) return null;

    return (
      <UploaderContext.Provider value={contextValue}>
        <Box className="noPadding" data-testid={testid}>
          {contextState.loading || isLoadingModal ? (
            <Spinner />
          ) : (
            <>
              {contextValue.progress < 100 && <Progress />}

              <Box ref={dropzoneRef} sx={dropzoneSx}>
                {!window.IN_MONITOR && (
                  <DropZone
                    ref={ref}
                    {...dropzoneOptions}
                    id={dropzoneId}
                    onClick={onClick}
                    isDropDisabled={allowDrop === false}
                    onDropAccepted={processFiles}
                    onDropRejected={console.warn}
                    maxFiles={undefined}
                    multiple
                    toolTip={title || dropzoneTooltip}
                    aria-label={ariaLabel}
                    aria-description={ariaDescription}
                    autoFocus={autoFocus}
                  />
                )}
              </Box>

              {(files.length ?? 0) < (maxFiles ?? Number.POSITIVE_INFINITY) &&
                noModify && <IconButton icon={FaFileUpload} disabled />}
            </>
          )}
          {children}
        </Box>
      </UploaderContext.Provider>
    );
  },
);

export default Uploader;
